// Swift Survey Module - Standalone bundle for user surveys
// Works independently on sites with or without Cognito authentication

const config = require('./swift-configs.js');
const configpubID = config[window.location.hostname]['Auth']['pubID'];

// Constants
const LOCAL_STORAGE_SURVEYS = configpubID + 'swiftSurveys';

let SWIFT_DEBUG_LOGGING = false;

/**
 * SwiftSurvey class - handles user survey display and tracking
 * Works standalone or integrates with SwiftCognito for auth checks
 */
class SwiftSurvey {
  constructor() {
    // Check for debugging
    if (window.location.href.toLowerCase().includes('swiftdebug=true')) {
      this.setCookie('swiftdebug', 'true', 365);
      SWIFT_DEBUG_LOGGING = true;
    }
    if (this.getCookie('swiftdebug') === 'true') {
      SWIFT_DEBUG_LOGGING = true;
    }

    SWIFT_DEBUG_LOGGING ? console.log('SwiftSurvey initialized') : null;

    // Defer survey initialization to ensure DOM is ready
    setTimeout(() => {
      this.initializeSurveyModule();
    }, 500);
  }

  // --- Survey Module ---
  initializeSurveyModule() {
    try {
      SWIFT_DEBUG_LOGGING ? console.log('Initializing survey module') : null;

      // Check if survey exists in DOM
      const surveyElement = document.getElementById('swift-survey-data');
      if (!surveyElement) {
        SWIFT_DEBUG_LOGGING ? console.log('No survey found on page') : null;
        return;
      }

      try {
        const surveyDataArray = JSON.parse(surveyElement.textContent);
        SWIFT_DEBUG_LOGGING ? console.log('Survey data found:', surveyDataArray) : null;

        // Handle both single survey object and array of surveys
        const surveys = Array.isArray(surveyDataArray) ? surveyDataArray : [surveyDataArray];

        // Find the best survey to show
        const surveyToShow = this.selectSurveyToShow(surveys);

        if (surveyToShow) {
          SWIFT_DEBUG_LOGGING ? console.log('Selected survey to show:', surveyToShow.surveyID) : null;
          this.renderSurveyNotice(surveyToShow);
          this.trackSurveyEvent('survey_notice_shown', surveyToShow);
        } else {
          SWIFT_DEBUG_LOGGING ? console.log('No eligible surveys to show') : null;
        }
      } catch (error) {
        console.error('Error parsing survey data:', error);
      }
    } catch (error) {
      console.error('Error initializing survey module:', error);
    }
  }

  // Select the best survey to show from an array
  // Priority: Show survey that ends soonest AND user hasn't interacted with
  selectSurveyToShow(surveys) {
    // Filter surveys that pass all qualification checks
    const eligibleSurveys = surveys.filter(survey => this.shouldShowSurvey(survey));

    if (eligibleSurveys.length === 0) {
      return null;
    }

    // Sort by closeDate ascending (earliest close date first)
    eligibleSurveys.sort((a, b) => {
      const dateA = new Date(a.closeDate);
      const dateB = new Date(b.closeDate);
      return dateA - dateB;
    });

    // Return survey with closest closeDate
    return eligibleSurveys[0];
  }

  shouldShowSurvey(surveyData) {
    const now = new Date();
    const startDate = new Date(surveyData.startDate);
    const closeDate = new Date(surveyData.closeDate);

    // Validate date parsing
    if (isNaN(startDate.getTime()) || isNaN(closeDate.getTime())) {
      console.error('Invalid survey dates. Dates must be in ISO 8601 format with timezone (e.g., 2025-10-06T00:00:00-06:00)');
      return false;
    }

    // Check date validity
    if (now < startDate || now > closeDate) {
      if (SWIFT_DEBUG_LOGGING) {
        console.log('Survey outside date range');
        console.log('Current time:', now.toISOString());
        console.log('Start date:', startDate.toISOString());
        console.log('Close date:', closeDate.toISOString());
      }
      return false;
    }

    // Check target user type - gracefully handle if SwiftCognito doesn't exist
    // Valid values: 'all', 'authenticated', 'anonymous'
    const isAuthenticated = this.isUserAuthenticated();
    const targetType = surveyData.targetUserType;

    if (targetType === 'authenticated' && !isAuthenticated) {
      SWIFT_DEBUG_LOGGING ? console.log('Survey targets authenticated users only') : null;
      return false;
    }
    if (targetType === 'anonymous' && isAuthenticated) {
      SWIFT_DEBUG_LOGGING ? console.log('Survey targets anonymous users only') : null;
      return false;
    }
    // targetType === 'all' passes through (no restrictions)

    // Check localStorage for user's survey state
    const surveyState = this.getSurveyState(surveyData.surveyID);
    if (surveyState) {
      const status = surveyState.status;

      // Never show again if completed or never
      if (status === 'completed' || status === 'never') {
        SWIFT_DEBUG_LOGGING ? console.log('Survey permanently dismissed or completed') : null;
        return false;
      }

      // Check if reminded and date hasn't passed
      if (status === 'reminded' && surveyState.remindDate) {
        const remindDate = new Date(surveyState.remindDate);
        if (now < remindDate) {
          SWIFT_DEBUG_LOGGING ? console.log('Survey remind date not reached') : null;
          return false;
        }
      }
    }

    // Check rate limiting (max 20 survey notices in 7 days)
    if (!this.isWithinSurveyRateLimit()) {
      SWIFT_DEBUG_LOGGING ? console.log('Survey rate limit exceeded') : null;
      return false;
    }

    return true;
  }

  isWithinSurveyRateLimit() {
    const surveyStorage = this.getSurveyStorage();
    if (!surveyStorage.meta || !surveyStorage.meta.noticeHistory) {
      return true;
    }

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const recentNotices = surveyStorage.meta.noticeHistory.filter(notice => {
      const noticeDate = new Date(notice.shownAt);
      return noticeDate > sevenDaysAgo;
    });

    return recentNotices.length < 20;
  }

  getSurveyStorage() {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_SURVEYS);
      return stored ? JSON.parse(stored) : { meta: { noticeHistory: [] } };
    } catch (error) {
      console.error('Error reading survey storage:', error);
      return { meta: { noticeHistory: [] } };
    }
  }

  getSurveyState(surveyID) {
    const storage = this.getSurveyStorage();
    return storage[surveyID] || null;
  }

  saveSurveyState(surveyID, state) {
    try {
      const storage = this.getSurveyStorage();
      storage[surveyID] = state;
      localStorage.setItem(LOCAL_STORAGE_SURVEYS, JSON.stringify(storage));
      SWIFT_DEBUG_LOGGING ? console.log('Survey state saved:', surveyID, state) : null;
    } catch (error) {
      console.error('Error saving survey state:', error);
    }
  }

  addSurveyNoticeToHistory(surveyID) {
    try {
      const storage = this.getSurveyStorage();
      if (!storage.meta) {
        storage.meta = { noticeHistory: [] };
      }
      if (!storage.meta.noticeHistory) {
        storage.meta.noticeHistory = [];
      }

      storage.meta.noticeHistory.push({
        surveyID: surveyID,
        shownAt: new Date().toISOString()
      });

      localStorage.setItem(LOCAL_STORAGE_SURVEYS, JSON.stringify(storage));
      SWIFT_DEBUG_LOGGING ? console.log('Survey notice added to history:', surveyID) : null;
    } catch (error) {
      console.error('Error adding survey notice to history:', error);
    }
  }

  renderSurveyNotice(surveyData) {
    const { surveyID, displayType, noticeHTML, surveyURL, showCloseButton } = surveyData;

    // Create notice container matching your widget structure
    const noticeContainer = document.createElement('div');
    noticeContainer.className = `survey-widget-container survey-widget-container-${displayType}`;
    noticeContainer.id = 'swift-survey-notice';
    noticeContainer.setAttribute('data-survey-id', surveyID);
    noticeContainer.setAttribute('data-display-type', displayType);

    if (SWIFT_DEBUG_LOGGING) {
      console.log('Survey showCloseButton:', showCloseButton);
      console.log('Close button will be:', showCloseButton ? 'shown' : 'hidden');
      console.log('Display type:', displayType);
    }

    // Special compact layout for inpage display type
    if (displayType === 'inpage') {
      const dismissLink = showCloseButton ? ' | <a href="#" data-action="close" class="survey-dismiss-link">Dismiss</a>' : '';
      noticeContainer.innerHTML = `
        <div class="survey-widget survey-widget-inpage" role="region" aria-label="Survey">
          <p class="survey-inpage-text">
            ${noticeHTML} <a href="${surveyURL}" target="_blank" data-action="take" class="survey-take-link">Take Survey</a>${dismissLink}
          </p>
          ${showCloseButton ? `
          <div class="survey-remind-options" style="display: none;">
            <p class="survey-remind-title">When should we remind you?</p>
            <div class="survey-remind-buttons">
              <button class="survey-remind-btn" data-days="1">Tomorrow</button>
              <button class="survey-remind-btn" data-days="2">In 2 days</button>
              <button class="survey-remind-btn" data-days="never">Never</button>
            </div>
          </div>` : ''}
        </div>
      `;
    } else {
      // Standard widget layout for banner, modal, slidein
      const closeButtonHTML = showCloseButton ? '<button class="survey-close" type="button" aria-label="Dismiss" data-action="close">×</button>' : '';

      // Conditionally build remind options (only if close button is shown)
      const remindOptionsHTML = showCloseButton ? `
          <div class="survey-remind-options" style="display: none;">
            <p class="survey-remind-title">When should we remind you?</p>
            <div class="survey-remind-buttons">
              <button class="survey-remind-btn" data-days="1">Tomorrow</button>
              <button class="survey-remind-btn" data-days="2">In 2 days</button>
              <button class="survey-remind-btn" data-days="never">Never</button>
            </div>
          </div>` : '';

      // Build notice content with your exact structure
      noticeContainer.innerHTML = `
        <div class="survey-widget survey-widget-${displayType}" role="region" aria-label="Reader survey">
          ${closeButtonHTML}
          <div class="survey-widget-content">
            ${noticeHTML}
            <a class="survey-cta" href="${surveyURL}" target="_blank" data-action="take">Take Survey</a>
          </div>
          ${remindOptionsHTML}
        </div>
      `;
    }

    // Remove any SVG icons that might have been injected
    setTimeout(() => {
      noticeContainer.querySelectorAll('.survey-widget > svg.svg-icon').forEach(el => el.remove());
    }, 0);

    // Add to DOM based on display type
    this.insertSurveyNoticeIntoDOM(noticeContainer, displayType);

    // Attach event listeners
    this.attachSurveyEventListeners(surveyData);

    // Track that notice was shown
    this.addSurveyNoticeToHistory(surveyID);
  }

  insertSurveyNoticeIntoDOM(noticeContainer, displayType) {
    switch (displayType) {
      case 'banner':
        // Wait for #survey-section to exist (with timeout)
        const waitForSurveySection = (callback, maxAttempts = 10, attempt = 0) => {
          const surveySection = document.getElementById('survey-section');
          if (surveySection) {
            callback(surveySection);
          } else if (attempt < maxAttempts) {
            setTimeout(() => waitForSurveySection(callback, maxAttempts, attempt + 1), 100);
          } else {
            if (SWIFT_DEBUG_LOGGING) {
              console.warn('#survey-section not found after waiting, appending to body top');
            }
            document.body.insertBefore(noticeContainer, document.body.firstChild);
          }
        };

        waitForSurveySection((surveySection) => {
          if (SWIFT_DEBUG_LOGGING) {
            console.log('Found #survey-section, parent is:', surveySection.parentElement?.tagName, surveySection.parentElement?.id);
            console.log('Inserting survey notice into #survey-section');
          }
          // Clear any existing content and append
          surveySection.innerHTML = '';
          surveySection.appendChild(noticeContainer);

          // Verify after insertion
          if (SWIFT_DEBUG_LOGGING) {
            setTimeout(() => {
              const check = document.getElementById('survey-section');
              console.log('After insertion, #survey-section parent is:', check?.parentElement?.tagName, check?.parentElement?.id);
            }, 100);
          }
        });
        break;
      case 'modal':
        document.body.appendChild(noticeContainer);
        break;
      case 'slidein':
        document.body.appendChild(noticeContainer);
        break;
      case 'inpage':
        // Check for #survey-section first (same as banner)
        const inpageSurveySection = document.getElementById('survey-section');
        if (inpageSurveySection) {
          if (SWIFT_DEBUG_LOGGING) {
            console.log('Inpage: Found #survey-section, inserting there');
          }
          inpageSurveySection.innerHTML = '';
          inpageSurveySection.appendChild(noticeContainer);
        } else {
          // Fallback: Try to insert after first paragraph or at top of content
          if (SWIFT_DEBUG_LOGGING) {
            console.log('Inpage: No #survey-section, using paragraph insertion');
          }
          const mainContent = document.querySelector('main') || document.querySelector('article') || document.body;
          const firstParagraph = mainContent.querySelector('p');
          if (firstParagraph) {
            firstParagraph.insertAdjacentElement('afterend', noticeContainer);
          } else {
            mainContent.insertBefore(noticeContainer, mainContent.firstChild);
          }
        }
        break;
      default:
        document.body.appendChild(noticeContainer);
    }
  }

  attachSurveyEventListeners(surveyData) {
    const noticeContainer = document.getElementById('swift-survey-notice');
    if (!noticeContainer) return;

    const { surveyID, closeDate } = surveyData;

    // Take survey button
    const takeButton = noticeContainer.querySelector('[data-action="take"]');
    if (takeButton) {
      takeButton.addEventListener('click', () => {
        this.handleSurveyTake(surveyID, closeDate);
      });
    }

    // Close button
    const closeButton = noticeContainer.querySelector('[data-action="close"]');
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.showRemindOptions(surveyID);
      });
    }

    // Remind option buttons
    const remindButtons = noticeContainer.querySelectorAll('.survey-remind-btn');
    remindButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const days = button.getAttribute('data-days');
        this.handleSurveyRemind(surveyID, closeDate, days);
      });
    });
  }

  showRemindOptions(surveyID) {
    const noticeContainer = document.getElementById('swift-survey-notice');
    if (!noticeContainer) return;

    const displayType = noticeContainer.getAttribute('data-display-type');

    if (displayType === 'inpage') {
      // For inpage, toggle between paragraph and remind options
      const paragraph = noticeContainer.querySelector('p');
      const remindOptions = noticeContainer.querySelector('.survey-remind-options');

      if (remindOptions && remindOptions.style.display === 'block') {
        // Already showing remind options, toggle back to survey
        if (paragraph) paragraph.style.display = 'block';
        remindOptions.style.display = 'none';
        SWIFT_DEBUG_LOGGING ? console.log('Toggled back to survey view') : null;
      } else {
        // Show remind options
        if (paragraph) paragraph.style.display = 'none';
        if (remindOptions) remindOptions.style.display = 'block';
        this.trackSurveyEvent('survey_close_clicked', { surveyID });
        SWIFT_DEBUG_LOGGING ? console.log('Showing remind options') : null;
      }
    } else {
      // For other display types, toggle between content and remind options
      const content = noticeContainer.querySelector('.survey-widget-content');
      const remindOptions = noticeContainer.querySelector('.survey-remind-options');

      if (remindOptions && remindOptions.style.display === 'block') {
        // Already showing remind options, toggle back to survey
        if (content) content.style.display = 'block';
        remindOptions.style.display = 'none';
        SWIFT_DEBUG_LOGGING ? console.log('Toggled back to survey view') : null;
      } else {
        // Show remind options
        if (content) content.style.display = 'none';
        if (remindOptions) remindOptions.style.display = 'block';
        this.trackSurveyEvent('survey_close_clicked', { surveyID });
        SWIFT_DEBUG_LOGGING ? console.log('Showing remind options') : null;
      }
    }
  }

  handleSurveyTake(surveyID, closeDate) {
    // Mark survey as completed
    this.saveSurveyState(surveyID, {
      status: 'completed',
      closeDate: closeDate,
      lastAction: new Date().toISOString(),
      actionType: 'taken',
      synced: false
    });

    // Track event
    this.trackSurveyEvent('survey_take_clicked', { surveyID });

    // Hide notice
    this.hideSurveyNotice();
  }

  handleSurveyRemind(surveyID, closeDate, days) {
    let status = 'reminded';
    let remindDate = null;

    if (days === 'never') {
      status = 'never';
    } else {
      const daysInt = parseInt(days, 10);
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + daysInt);
      remindDate = futureDate.toISOString();
    }

    // Save state
    this.saveSurveyState(surveyID, {
      status: status,
      remindDate: remindDate,
      closeDate: closeDate,
      lastAction: new Date().toISOString(),
      actionType: status === 'never' ? 'never' : 'reminded',
      synced: false
    });

    // Track event
    const eventName = status === 'never' ? 'survey_never_clicked' : 'survey_remind_selected';
    this.trackSurveyEvent(eventName, { surveyID, remindOption: days });

    // Hide notice
    this.hideSurveyNotice();
  }

  hideSurveyNotice() {
    const noticeContainer = document.getElementById('swift-survey-notice');
    if (noticeContainer) {
      noticeContainer.remove();
    }
  }

  trackSurveyEvent(eventName, data) {
    try {
      // Send to GA4
      if (typeof gtag !== 'undefined') {
        const isAuthenticated = this.isUserAuthenticated();
        gtag('event', eventName, {
          survey_id: data.surveyID || data.survey_id || 'unknown',
          remind_option: data.remindOption || undefined,
          user_type: isAuthenticated ? 'authenticated' : 'anonymous'
        });
        SWIFT_DEBUG_LOGGING ? console.log('Survey GA4 event sent:', eventName, data) : null;
      }
    } catch (error) {
      console.error('Error tracking survey event:', error);
    }
  }

  // --- Authentication Check ---
  // Gracefully checks if user is authenticated via SwiftCognito bundle
  // Returns false if SwiftCognito doesn't exist (non-Cognito sites)
  isUserAuthenticated() {
    try {
      // Try to check via SwiftCognito global if it exists
      if (window.Swiftcognito && typeof window.Swiftcognito.isAuthenticated === 'function') {
        return window.Swiftcognito.isAuthenticated();
      }

      // Fallback: check cookie directly (same method as SwiftCognito uses)
      const COOKIE_NAME_LOGGED_IN = 'swiftCognitoHasLoggedIn';
      return this.getCookie(COOKIE_NAME_LOGGED_IN) === 'true';
    } catch (error) {
      SWIFT_DEBUG_LOGGING ? console.log('Auth check failed, assuming anonymous:', error) : null;
      return false;
    }
  }

  // --- Helper Methods ---
  setCookie(name, value, days) {
    var expires = "";
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }

  getCookie(name) {
    var nameEQ = name + "=";
    var ca = document.cookie.split(';');
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }
}

// Initialize SwiftSurvey when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.SwiftSurvey = new SwiftSurvey();
  });
} else {
  window.SwiftSurvey = new SwiftSurvey();
}

// Export for webpack
module.exports = SwiftSurvey;
